import { apiFetch } from "./apiClient";

/** Maps to the backend's MenuRolePermissionDto — one row per
 * (accountId, roleId, menuId) that has ever been saved. */
export interface MenuRolePermissionDto {
  permissionId: number;
  accountId: number;
  roleId: number;
  roleName: string | null;
  menuId: number;
  menuDisplayName: string | null;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canPrint: boolean;
  canExport: boolean;
  createdOn: string;
}

/** Shape accepted by both create and update (matches
 * CreateMenuRolePermissionDto/UpdateMenuRolePermissionDto). */
export interface MenuRolePermissionPayload {
  accountId: number;
  roleId: number;
  menuId: number;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canPrint: boolean;
  canExport: boolean;
}

/** One row per active menu module, annotated with whatever permissions the
 * given role already has (all flags false for modules with no saved row
 * yet) — matches the backend's GET .../grid, purpose-built for rendering a
 * role's full permission matrix in one call. */
export interface MenuPermissionGridItemDto {
  menuId: number;
  menuDisplayName: string;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canPrint: boolean;
  canExport: boolean;
}

/** One row of the bulk-save payload (matches MenuPermissionItemDto). */
export interface MenuPermissionItem {
  menuId: number;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canPrint: boolean;
  canExport: boolean;
}

export const listMenuRolePermissions = async (): Promise<
  MenuRolePermissionDto[]
> => {
  const res = await apiFetch("/menurolepermissions");
  return res.json();
};

export const getMenuRolePermission = async (
  id: number
): Promise<MenuRolePermissionDto> => {
  const res = await apiFetch(`/menurolepermissions/${id}`);
  return res.json();
};

export const createMenuRolePermission = async (
  payload: MenuRolePermissionPayload
): Promise<MenuRolePermissionDto> => {
  const res = await apiFetch("/menurolepermissions", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const updateMenuRolePermission = async (
  id: number,
  payload: MenuRolePermissionPayload
): Promise<void> => {
  await apiFetch(`/menurolepermissions/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
};

export const deleteMenuRolePermission = async (id: number): Promise<void> => {
  await apiFetch(`/menurolepermissions/${id}`, { method: "DELETE" });
};

export const getPermissionGrid = async (
  accountId: number,
  roleId: number
): Promise<MenuPermissionGridItemDto[]> => {
  const res = await apiFetch(
    `/menurolepermissions/grid?accountId=${accountId}&roleId=${roleId}`
  );
  return res.json();
};

/** Upserts an entire role's permission grid in one call — one row per
 * module, matching what the Roles page's Save button submits. */
export const bulkSaveMenuRolePermissions = async (
  accountId: number,
  roleId: number,
  permissions: MenuPermissionItem[]
): Promise<MenuRolePermissionDto[]> => {
  const res = await apiFetch("/menurolepermissions/bulk", {
    method: "POST",
    body: JSON.stringify({ accountId, roleId, permissions }),
  });
  return res.json();
};
